import React, { useEffect } from 'react';

export default function AttendanceSwipesModal({
  isOpen,
  onClose,
  record,
}) {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !record) return null;

  const swipes = record.swipes || [];
  const cleanDate = String(record.dateDisplay || record.date || '').replace(/\s+12:00:00\s*(AM|PM)?/i, '');

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-xs transition-opacity duration-200"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="relative z-10 flex flex-col w-full max-w-2xl overflow-hidden rounded-2xl bg-surface-container-lowest shadow-2xl border border-outline-variant/30 animate-in fade-in zoom-in-95 duration-200">
        {/* Top Header Banner */}
        <div className="flex items-center justify-between bg-primary px-5 py-3.5 text-on-primary">
          <div className="flex items-center gap-2.5">
            <span className="material-symbols-outlined text-xl text-white/90">history</span>
            <div>
              <h2 className="font-display text-base font-bold text-white leading-tight">
                Attendance History — Swipe Details
              </h2>
              <p className="text-xs text-white/80 font-medium">
                {cleanDate} • {record.name} {record.empId ? `(${record.empId})` : ''}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-white/80 hover:bg-white/10 hover:text-white transition cursor-pointer"
          >
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>

        {/* Swipe Records Table with Thin Grid Lines separating rows and columns */}
        <div className="p-5 overflow-x-auto">
          <table className="w-full text-left text-xs border border-outline-variant/25 border-collapse">
            <thead>
              <tr className="bg-surface-container-low text-[10px] font-extrabold uppercase tracking-wider text-black">
                <th className="border border-outline-variant/25 py-2.5 px-4 text-black">EmpId</th>
                <th className="border border-outline-variant/25 py-2.5 px-4 text-black">Name</th>
                <th className="border border-outline-variant/25 py-2.5 px-4 text-black">Date</th>
                <th className="border border-outline-variant/25 py-2.5 px-4 text-black">Swipes (Time)</th>
                <th className="border border-outline-variant/25 py-2.5 px-4 text-black">MachInNo</th>
              </tr>
            </thead>
            <tbody>
              {swipes.length === 0 ? (
                <tr>
                  <td colSpan={5} className="border border-outline-variant/20 py-8 text-center text-slate-600">
                    <div className="flex flex-col items-center justify-center gap-1.5">
                      <span className="material-symbols-outlined text-3xl text-outline-variant">
                        event_busy
                      </span>
                      <p className="font-medium text-xs text-black">No swipe records found for this date.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                swipes.map((swipe, idx) => {
                  const cleanSwipeDate = String(swipe.date || '').replace(/\s+12:00:00\s*(AM|PM)?/i, '');
                  return (
                    <tr
                      key={swipe.id || idx}
                      className={idx % 2 === 0 ? 'bg-surface-container-low/20' : 'bg-surface-container-lowest'}
                    >
                      <td className="border border-outline-variant/20 py-2.5 px-4 font-sans tabular-nums not-italic font-bold text-xs text-black">
                        {swipe.empId}
                      </td>
                      <td className="border border-outline-variant/20 py-2.5 px-4 font-bold text-xs text-black font-sans not-italic">
                        {swipe.name}
                      </td>
                      <td className="border border-outline-variant/20 py-2.5 px-4 text-xs font-sans tabular-nums not-italic font-medium text-black">
                        {cleanSwipeDate}
                      </td>
                      <td className="border border-outline-variant/20 py-2.5 px-4">
                        <span className="inline-flex items-center gap-1 font-sans tabular-nums not-italic font-bold text-xs text-primary">
                          <span className="material-symbols-outlined text-sm">schedule</span>
                          {swipe.swipes}
                        </span>
                      </td>
                      <td className="border border-outline-variant/20 py-2.5 px-4 text-xs text-black font-sans tabular-nums not-italic font-semibold">
                        {swipe.machInNo || '0'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-between border-t border-outline-variant/20 bg-surface-container-low/40 px-5 py-3">
          <span className="text-xs text-on-surface-variant font-medium">
            Total Swipes Recorded: <strong className="text-on-surface font-sans tabular-nums not-italic">{swipes.length}</strong>
          </span>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-outline-variant/40 bg-surface-container-lowest px-3.5 py-1.5 text-xs font-bold text-on-surface hover:bg-surface-container-high transition cursor-pointer shadow-xs"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
